import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';

import ScrollToTop from './utils/scrollToTop';
import CookieConsentModal from './utils/cookiePopup/cookiePopup';
import StructuredData from './utils/hoc/structuredData';

import Header from './components/header';
import Footer from './components/footer';
import ScrollTopButton from './components/scrollTopButton';
import BookAppointmentButton from './components/bookAppointmentButton/bookAppointmentButton';
import AppRoutes from './utils/appRoutes/approutes';

import './i18n';
import 'bootstrap/dist/css/bootstrap.min.css';
import './global.scss';
import { AOSWrapper } from './utils/hoc/aosWrapper';

const App = () => {
  const myRef = useRef(null);

  // ✅ initialize from localStorage
  const [isAnalyticsAllowed, setAnalyticsAllowed] = useState(
    localStorage.getItem('cookieConsent') === 'accepted',
  );

  // Was: a second `gtag('consent', 'default', ...)` call fired here on
  // every mount, duplicating the one in public/index.html's inline script
  // - confirmed live 30 Aug 2026 via window.dataLayer, and matches a GTM
  // audit finding (5 Aug 2026) of duplicate consent events on every page
  // load. The index.html call is the one that actually matters for
  // Google's Consent Mode: it has to run BEFORE gtm.js loads and any tag
  // fires, which is only guaranteed for a plain inline <script> in <head>
  // - by the time this component mounts, React's bundle has already
  // downloaded and GTM may already be initializing, so this copy was
  // redundant at best and a race at worst. Removed; default consent is
  // set exactly once now, in public/index.html.

  // ✅ update consent whenever state changes
  useEffect(() => {
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: isAnalyticsAllowed ? 'granted' : 'denied',
      });
    }
  }, [isAnalyticsAllowed]);

  return (
    <Router>
      <AOSWrapper />
      <ScrollToTop />
      <StructuredData />

      <CookieConsentModal onConsentChange={setAnalyticsAllowed} />

      <BookAppointmentButton />
      <Header myRef={myRef} />

      <div className="myBody">
        {/* i18n.js only preloads the current page's own translation
            namespace up front (not all 18) - see the comment on
            CORE_NAMESPACES in i18n.js. A <Suspense> boundary here was tried
            as the safety net for a client-side navigation to a page whose
            namespace hasn't been fetched yet, but that introduced a real,
            reproducible hydration mismatch ("Minified React error #418")
            during the very FIRST hydration of every page - react-snap's
            prerendered HTML has no server-Suspense dehydration markers (it's
            a plain headless-browser snapshot, not renderToString/
            renderToPipeableStream output), and React's hydration algorithm
            for a <Suspense> boundary expects those markers even when nothing
            actually suspends. Reverted: this tree is intentionally identical
            in shape to before, which is what hydrates cleanly. i18n.js sets
            react: { useSuspense: false } instead, so a not-yet-loaded
            namespace during in-app navigation never suspends at all - it
            just renders the translation key literally for the one render
            until the namespace fetch resolves, then re-renders with the
            real text. That's a much smaller, self-healing visual blip
            instead of a full-page crash, and it doesn't touch hydration at
            all. */}
        <AppRoutes myRef={myRef} />
      </div>

      <Footer />
      <ScrollTopButton />
    </Router>
  );
};

export default App;
